import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, API } from "@/lib/rn3";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import InviteClientButton from "@/components/InviteClientButton";
import {
  ArrowLeft, Plus, Search, X, Loader2, TrendingUp, AlertTriangle, Users, Activity, Percent,
  Phone, Mail, Calendar as CalIcon, FileText, MessageSquare, UserPlus, Trash2, Bell, QrCode, Download,
  BarChart3, ChevronDown, ChevronUp,
} from "lucide-react";

const STATUS_LABELS = {
  new_lead:               { en: "New Lead",              es: "Nuevo Prospecto",        color: "bg-[#737373]" },
  contacted:              { en: "Contacted",             es: "Contactado",             color: "bg-[#3B82F6]" },
  intake_started:         { en: "Intake Started",        es: "Intake Iniciado",        color: "bg-[#8B5CF6]" },
  intake_completed:       { en: "Intake Completed",      es: "Intake Completo",        color: "bg-[#A855F7]" },
  appointment_scheduled:  { en: "Appointment Scheduled", es: "Cita Programada",        color: "bg-[#C4A252]" },
  proposal_sent:          { en: "Proposal Sent",         es: "Propuesta Enviada",      color: "bg-[#F97316]" },
  active_client:          { en: "Active Client",         es: "Cliente Activo",         color: "bg-[#15803D]" },
  completed:              { en: "Completed",             es: "Completado",             color: "bg-[#0A0A0A]" },
  lost_lead:              { en: "Lost Lead",             es: "Prospecto Perdido",      color: "bg-[#8B1C22]" },
};
const STATUS_KEYS = Object.keys(STATUS_LABELS);
const SOURCES = [
  { key: "qr_code",  en: "QR Code",  es: "Código QR" },
  { key: "website",  en: "Website",  es: "Sitio web" },
  { key: "facebook", en: "Facebook", es: "Facebook" },
  { key: "referral", en: "Referral", es: "Referencia" },
  { key: "walk_in",  en: "Walk-In",  es: "Visita en persona" },
  { key: "other",    en: "Other",    es: "Otro" },
];
const SERVICES = [
  { key: "bookkeeping", label: "Bookkeeping" },
  { key: "tax_prep", label: "Tax Prep" },
  { key: "sales_tax", label: "Sales Tax" },
  { key: "notary", label: "Notary" },
  { key: "loan_signing", label: "Loan Signing" },
  { key: "itin", label: "ITIN" },
  { key: "translation", label: "Translation" },
  { key: "general", label: "General" },
  { key: "consultation", label: "Consultation" },
];

const StatCard = ({ icon: Icon, label, value, accent = "", testid }) => (
  <div className="rn3-card" data-testid={testid}>
    <Icon className={`w-6 h-6 ${accent || "text-[#C4A252]"}`} />
    <div className="mt-3 text-3xl font-serif-display text-[#0A0A0A]">{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#737373] mt-1">{label}</div>
  </div>
);

const StatusPill = ({ status }) => {
  const meta = STATUS_LABELS[status] || { en: status, color: "bg-[#737373]" };
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 text-white ${meta.color}`} data-testid={`lead-status-pill-${status}`}>
      {meta.en}
    </span>
  );
};

const isOverdue = (d) => {
  if (!d) return false;
  const today = new Date().toISOString().slice(0, 10);
  return String(d) < today;
};

const LeadFormModal = ({ open, onClose, existing, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(existing ? { ...existing } : {
      name: "", business_name: "", email: "", phone: "", service_requested: "general",
      source: "walk_in", source_detail: "", status: "new_lead", notes: "", follow_up_date: "",
    });
  }, [open, existing]);
  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      // strip empty optionals so backend accepts
      ["email", "phone", "source_detail", "notes", "follow_up_date", "lost_reason", "business_name"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      const { data } = existing
        ? await api.patch(`/admin/leads/${existing.id}`, payload)
        : await api.post(`/admin/leads`, payload);
      toast.success(existing ? "Lead updated" : "Lead created");
      onSaved?.(data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="lead-form-modal">
      <div className="bg-white max-w-2xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <h3 className="font-serif-display text-2xl">{existing ? "Edit Lead" : "New Lead"}</h3>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className="rn3-eyebrow block mb-2">Name</label><input required value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="rn3-input w-full" data-testid="lead-form-name" /></div>
          <div className="md:col-span-2"><label className="rn3-eyebrow block mb-2">Business Name <span className="text-[#737373] normal-case">(if applicable)</span></label><input value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} className="rn3-input w-full" data-testid="lead-form-business-name" /></div>
          <div><label className="rn3-eyebrow block mb-2">Email</label><input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className="rn3-input w-full" data-testid="lead-form-email" /></div>
          <div><label className="rn3-eyebrow block mb-2">Phone</label><input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className="rn3-input w-full" data-testid="lead-form-phone" /></div>
          <div><label className="rn3-eyebrow block mb-2">Service Requested</label>
            <select value={form.service_requested || ""} onChange={(e) => set("service_requested", e.target.value)} className="rn3-input w-full" data-testid="lead-form-service">
              <option value="">—</option>
              {SERVICES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div><label className="rn3-eyebrow block mb-2">Source</label>
            <select value={form.source || "other"} onChange={(e) => set("source", e.target.value)} className="rn3-input w-full" data-testid="lead-form-source">
              {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.en}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><label className="rn3-eyebrow block mb-2">Source Detail <span className="text-[#737373] normal-case">(e.g. QR campaign name, who referred)</span></label><input value={form.source_detail || ""} onChange={(e) => set("source_detail", e.target.value)} className="rn3-input w-full" data-testid="lead-form-source-detail" /></div>
          <div><label className="rn3-eyebrow block mb-2">Status</label>
            <select value={form.status || "new_lead"} onChange={(e) => set("status", e.target.value)} className="rn3-input w-full" data-testid="lead-form-status">
              {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_LABELS[k].en}</option>)}
            </select>
          </div>
          <div><label className="rn3-eyebrow block mb-2">Follow-Up Date</label><input type="date" value={form.follow_up_date || ""} onChange={(e) => set("follow_up_date", e.target.value)} className="rn3-input w-full" data-testid="lead-form-followup" /></div>
          {form.status === "lost_lead" && (
            <div className="md:col-span-2"><label className="rn3-eyebrow block mb-2">Lost Reason</label><input value={form.lost_reason || ""} onChange={(e) => set("lost_reason", e.target.value)} className="rn3-input w-full" data-testid="lead-form-lost-reason" /></div>
          )}
          <div className="md:col-span-2"><label className="rn3-eyebrow block mb-2">Notes</label><textarea rows={3} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="rn3-input w-full" data-testid="lead-form-notes" /></div>
          <div className="md:col-span-2 flex items-center justify-end gap-2 pt-3 border-t border-[#E5E5E5] mt-2">
            <button type="button" onClick={onClose} className="text-sm text-[#737373] hover:text-[#0A0A0A] uppercase tracking-widest">Cancel</button>
            <button disabled={busy} className="rn3-btn-primary" data-testid="lead-form-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {existing ? "Save" : "Create Lead"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const LeadDetailDrawer = ({ open, onClose, lead, onUpdated }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open || !lead) return null;

  const appendNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/leads/${lead.id}/notes`, { body: note }); setNote(""); onUpdated?.(); toast.success("Note added"); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const convert = async () => {
    if (!lead.email) { toast.error("Lead needs an email first"); return; }
    if (!window.confirm(`Convert ${lead.name} into a client?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/convert`, { create_user: true });
      if (data.temporary_password) {
        const msg = `Client created — temporary password: ${data.temporary_password}\n\nCopy now; this is the only time it will be shown.`;
        window.alert(msg);
        toast.success("Converted to client — temp password shown");
      } else if (data.user_existed) {
        toast.success("Linked to existing client user");
      } else {
        toast.success("Converted to client");
      }
      onUpdated?.();
    }
    catch (err) { toast.error(err.response?.data?.detail || "Convert failed"); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await api.delete(`/admin/leads/${lead.id}`); toast.success("Lead deleted"); onUpdated?.(true); onClose(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/40" onClick={onClose} data-testid="lead-detail-drawer">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto border-l border-[#E5E5E5]" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white flex items-center justify-between">
          <div>
            <div className="rn3-eyebrow">Lead</div>
            <h3 className="font-serif-display text-2xl mt-1">{lead.name}</h3>
            {lead.business_name && <div className="text-sm text-[#737373] mt-0.5 italic" data-testid="lead-drawer-business-name">{lead.business_name}</div>}
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <StatusPill status={lead.status} />
            <div className="text-xs text-[#737373]">Score: <span className="font-bold text-[#0A0A0A] text-base">{lead.lead_score ?? 0}</span>/100</div>
          </div>
          <div className="space-y-2 text-sm">
            {lead.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#737373]" /><a href={`mailto:${lead.email}`} className="text-[#0A0A0A] hover:text-[#8B1C22]">{lead.email}</a></div>}
            {lead.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#737373]" /><a href={`tel:${lead.phone}`} className="text-[#0A0A0A] hover:text-[#8B1C22]">{lead.phone}</a></div>}
            {lead.service_requested && <div className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-[#737373]" /><span className="capitalize">{lead.service_requested.replace(/_/g, " ")}</span></div>}
            <div className="flex items-center gap-2"><Activity className="w-3.5 h-3.5 text-[#737373]" /><span>{SOURCES.find((s) => s.key === lead.source)?.en || lead.source}{lead.source_detail ? ` · ${lead.source_detail}` : ""}</span></div>
            {lead.follow_up_date && <div className={`flex items-center gap-2 ${isOverdue(lead.follow_up_date) ? "text-[#8B1C22] font-medium" : ""}`}><CalIcon className="w-3.5 h-3.5" /> Follow-up: {lead.follow_up_date}{isOverdue(lead.follow_up_date) ? " · OVERDUE" : ""}</div>}
          </div>
          {lead.notes && <div className="text-sm text-[#404040] border-l-2 border-[#C4A252] pl-3 whitespace-pre-line">{lead.notes}</div>}

          <div>
            <div className="rn3-eyebrow mb-3">Timeline</div>
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {(lead.timeline || []).slice().reverse().map((e, i) => (
                <li key={i} className="text-xs text-[#404040] border-b border-[#F3EFE9] pb-2" data-testid={`lead-timeline-${i}`}>
                  <span className="text-[#737373]">{(e.at || "").replace("T", " ").slice(0, 16)}</span>
                  <span className="ml-2 uppercase tracking-widest text-[10px] text-[#8B1C22]">{e.kind}</span>
                  <div className="mt-1">{e.body}</div>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <label className="rn3-eyebrow block mb-2">Add Note</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rn3-input w-full" data-testid="lead-note-input" placeholder="Called and left voicemail…" />
            <div className="mt-2 flex justify-end">
              <button onClick={appendNote} disabled={busy || !note.trim()} className="rn3-btn-gold" data-testid="lead-note-submit"><MessageSquare className="w-4 h-4" /> Append</button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 pt-4 border-t border-[#E5E5E5]">
            <button onClick={remove} className="text-xs uppercase tracking-widest text-[#8B1C22] hover:text-[#6F1419] inline-flex items-center gap-1" data-testid="lead-delete-btn"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            {lead.status !== "active_client" && lead.status !== "completed" && (
              <button onClick={convert} disabled={busy} className="rn3-btn-primary" data-testid="lead-convert-btn"><UserPlus className="w-4 h-4" /> Convert to Client</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SOURCE_LABEL = {
  qr_code: "QR Code", website: "Website", facebook: "Facebook",
  referral: "Referral", walk_in: "Walk-In", other: "Other", unknown: "Unknown",
};
const SOURCE_COLORS = {
  qr_code: "#8B1C22",   // deep red — brand primary
  website: "#C4A252",   // gold — brand accent
  facebook: "#3B82F6",  // blue
  referral: "#15803D",  // green
  walk_in: "#A855F7",   // purple
  other:   "#737373",   // muted gray
};
const SOURCE_KEYS = ["qr_code", "website", "facebook", "referral", "walk_in", "other"];

const LeadTimeSeriesChart = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/leads/timeseries", { params: { days } });
        if (!cancelled) setData(data);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const series = data?.series || [];
  const totals = data?.totals_by_source || {};
  const grand = data?.grand_total ?? 0;
  // Format date label for x-axis: "Jun 18"
  const formatTick = (d) => {
    try { const dt = new Date(d + "T00:00:00Z"); return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }); }
    catch { return d; }
  };
  // Show roughly 6-8 x-ticks regardless of range
  const tickStep = Math.max(1, Math.floor(series.length / 7));
  const xTicks = series.filter((_, i) => i % tickStep === 0).map((r) => r.date);

  return (
    <div className="px-5 py-5 border-t border-[#E5E5E5]" data-testid="lead-timeseries">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="rn3-eyebrow">Daily Leads · Stacked by Source</div>
          <div className="text-xs text-[#737373] mt-1">
            {grand} {grand === 1 ? "lead" : "leads"} captured in the last {days} days
            {loading && <Loader2 className="inline w-3 h-3 animate-spin ml-2 text-[#C4A252]" />}
          </div>
        </div>
        <div className="inline-flex border border-[#E5E5E5] bg-white" data-testid="ts-range-toggle">
          {[30, 60, 90].map((n) => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition ${days === n ? "bg-[#0A0A0A] text-white" : "text-[#737373] hover:bg-[#FAFAFA]"}`}
              data-testid={`ts-range-${n}`}
            >
              {n}d
            </button>
          ))}
        </div>
      </div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <AreaChart data={series} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
            <defs>
              {SOURCE_KEYS.map((k) => (
                <linearGradient key={k} id={`grad-${k}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SOURCE_COLORS[k]} stopOpacity={0.85} />
                  <stop offset="100%" stopColor={SOURCE_COLORS[k]} stopOpacity={0.35} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="#F1F1F1" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              ticks={xTicks}
              tickFormatter={formatTick}
              stroke="#A3A3A3"
              tick={{ fontSize: 10, fill: "#737373" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              allowDecimals={false}
              stroke="#A3A3A3"
              tick={{ fontSize: 10, fill: "#737373" }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip
              labelFormatter={(d) => formatTick(d)}
              contentStyle={{ background: "#fff", border: "1px solid #E5E5E5", borderRadius: 0, fontSize: 12 }}
              itemStyle={{ padding: "2px 0" }}
              formatter={(v, name) => [v, SOURCE_LABEL[name] || name]}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              iconType="square"
              formatter={(name) => `${SOURCE_LABEL[name] || name} (${totals[name] || 0})`}
            />
            {SOURCE_KEYS.map((k) => (
              <Area
                key={k}
                type="monotone"
                dataKey={k}
                stackId="1"
                stroke={SOURCE_COLORS[k]}
                strokeWidth={1.2}
                fill={`url(#grad-${k})`}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const SourceBreakdownPanel = ({ breakdown, open, onToggle }) => {
  if (!breakdown) return null;
  const { by_source = [], by_campaign = [], intakes = {}, leads = {} } = breakdown;
  const maxSource = Math.max(1, ...by_source.map((r) => r.total));
  const maxCampaign = Math.max(1, ...by_campaign.map((r) => r.total));
  return (
    <div className="mt-6 border border-[#E5E5E5] bg-white" data-testid="source-breakdown-panel">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#FDFBF7] transition"
        data-testid="source-breakdown-toggle"
      >
        <div className="flex items-center gap-3">
          <BarChart3 className="w-4 h-4 text-[#8B1C22]" />
          <div className="text-left">
            <div className="rn3-eyebrow">Marketing Attribution</div>
            <div className="text-sm font-semibold mt-0.5">Source &amp; Campaign Breakdown</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-5 text-xs text-[#737373]">
            <div data-testid="bd-intake-summary"><span className="font-semibold text-[#0A0A0A]">{intakes.total ?? 0}</span> intakes <span className="text-[#A3A3A3]">({intakes.last_30d ?? 0} · 30d)</span></div>
            <div data-testid="bd-lead-summary"><span className="font-semibold text-[#0A0A0A]">{leads.total ?? 0}</span> leads <span className="text-[#A3A3A3]">({leads.last_30d ?? 0} · 30d)</span></div>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-[#737373]" /> : <ChevronDown className="w-4 h-4 text-[#737373]" />}
        </div>
      </button>
      {open && (
        <>
          <LeadTimeSeriesChart />
          <div className="grid md:grid-cols-2 gap-6 px-5 pb-5 pt-2 border-t border-[#E5E5E5]">
          <div>
            <div className="rn3-eyebrow mb-3">Leads by Source</div>
            {by_source.length === 0 && <div className="text-xs text-[#737373] py-4">No leads yet.</div>}
            <div className="space-y-2">
              {by_source.map((r) => (
                <div key={r.source} className="text-sm" data-testid={`source-row-${r.source}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium">{SOURCE_LABEL[r.source] || r.source}</div>
                    <div className="text-xs text-[#737373]">
                      <span className="font-semibold text-[#0A0A0A]">{r.total}</span> · {r.converted} won · {r.conversion_rate}%
                    </div>
                  </div>
                  <div className="h-2 bg-[#FAFAFA] border border-[#E5E5E5]">
                    <div className="h-full bg-[#8B1C22]" style={{ width: `${(r.total / maxSource) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="rn3-eyebrow mb-3">Top Campaigns <span className="text-[#737373] normal-case">(by src_detail)</span></div>
            {by_campaign.length === 0 && <div className="text-xs text-[#737373] py-4">No tagged campaigns yet. Print QRs from the modal above — each scan will be attributed here.</div>}
            <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
              {by_campaign.map((r) => (
                <div key={`${r.source}-${r.source_detail}`} className="text-sm" data-testid={`campaign-row-${r.source_detail}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium truncate" title={r.source_detail}>
                      <span className="text-[10px] uppercase tracking-wider text-[#737373] mr-1.5">{SOURCE_LABEL[r.source] || r.source}</span>
                      {r.source_detail}
                    </div>
                    <div className="text-xs text-[#737373] flex-shrink-0 ml-2">
                      <span className="font-semibold text-[#0A0A0A]">{r.total}</span> · {r.converted} won · {r.conversion_rate}%
                    </div>
                  </div>
                  <div className="h-2 bg-[#FAFAFA] border border-[#E5E5E5]">
                    <div className="h-full bg-[#C4A252]" style={{ width: `${(r.total / maxCampaign) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
};



const QRGeneratorModal = ({ open, onClose }) => {
  const [campaign, setCampaign] = useState("");
  const [target, setTarget] = useState("intake");
  const [size, setSize] = useState(800);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let lastUrl = "";
    const fetchPreview = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/qr/intake.png`, { params: { campaign, target, size }, responseType: "blob" });
        if (cancelled) return;
        lastUrl = window.URL.createObjectURL(data);
        setPreviewUrl((prev) => { if (prev) window.URL.revokeObjectURL(prev); return lastUrl; });
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    };
    const id = setTimeout(fetchPreview, 250);
    return () => { cancelled = true; clearTimeout(id); if (lastUrl) window.URL.revokeObjectURL(lastUrl); };
  }, [open, campaign, target, size]);

  if (!open) return null;
  const download = async () => {
    try {
      const { data } = await api.get(`/admin/qr/intake.png`, { params: { campaign, target, size }, responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `RN3_QR_${target}_${campaign || "default"}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Download failed"); }
  };
  const downloadBatch = async () => {
    try {
      toast.message("Building batch ZIP…");
      const { data } = await api.get(`/admin/qr/batch.zip`, { params: { target, size, prefix: campaign || "service" }, responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `RN3_QR_batch_${target}_${campaign || "service"}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
      toast.success("Service batch downloaded — 7 PNGs ready to print.");
    } catch { toast.error("Batch download failed"); }
  };
  const downloadPrintSheet = async () => {
    try {
      toast.message("Building print sheet PDF…");
      const { data } = await api.get(`/admin/qr/print-sheet.pdf`, { params: { target, prefix: campaign || "service" }, responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `RN3_QR_print_sheet_${target}_${campaign || "service"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
      toast.success("Print sheet ready — 8.5×11\" PDF.");
    } catch { toast.error("Print sheet download failed"); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="qr-modal">
      <div className="bg-white max-w-3xl w-full border border-[#E5E5E5] shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white">
          <div><div className="rn3-eyebrow"><QrCode className="w-3 h-3 inline mr-1" /> Marketing QR</div>
            <h3 className="font-serif-display text-2xl mt-1">QR Code Generator</h3>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#8B1C22]"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-6 p-6">
          <div className="space-y-4">
            <div>
              <label className="rn3-eyebrow block mb-2">Campaign Tag</label>
              <input value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="e.g. flyer-2026, business-card, food-truck" className="rn3-input w-full" data-testid="qr-campaign" />
              <div className="text-xs text-[#737373] mt-1">Stored as <code>src_detail</code> on the lead — pick something specific so you can attribute every scan.</div>
            </div>
            <div>
              <label className="rn3-eyebrow block mb-2">Landing Page</label>
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="rn3-input w-full" data-testid="qr-target">
                <optgroup label="General">
                  <option value="intake">Intake form (generic)</option>
                  <option value="appointments">Book appointment</option>
                  <option value="welcome">Welcome / landing</option>
                </optgroup>
                <optgroup label="Per-Service Intake">
                  <option value="bookkeeping">Bookkeeping</option>
                  <option value="tax_prep">Tax Preparation</option>
                  <option value="sales_tax">Sales Tax Reporting</option>
                  <option value="notary">Notary Services</option>
                  <option value="loan_signing">Loan Signing Agent</option>
                  <option value="itin">ITIN Acceptance Agent</option>
                  <option value="translation">Spanish Translation</option>
                </optgroup>
              </select>
              <div className="text-[10px] text-[#737373] mt-1.5">
                Per-service options deep-link to the intake form with that service pre-selected (e.g. <code>/intake?service=bookkeeping</code>).
              </div>
            </div>
            <div>
              <label className="rn3-eyebrow block mb-2">Size · {size}px</label>
              <input type="range" min="400" max="1600" step="100" value={size} onChange={(e) => setSize(Number(e.target.value))} className="w-full" data-testid="qr-size" />
            </div>
            <div className="pt-3 border-t border-[#E5E5E5]">
              <button onClick={download} className="rn3-btn-primary w-full justify-center" data-testid="qr-download"><Download className="w-4 h-4" /> Download PNG</button>
              <div className="text-xs text-[#737373] mt-2">Print on flyers, business cards, or counter signage. Every scan auto-creates a Lead tagged to this campaign.</div>
              <div className="mt-4 pt-4 border-t border-dashed border-[#E5E5E5]">
                <div className="rn3-eyebrow mb-2">Pre-Made Service Batch</div>
                <button onClick={downloadBatch} className="rn3-btn-gold w-full justify-center" data-testid="qr-download-batch"><Download className="w-4 h-4" /> Download Service Batch (ZIP)</button>
                <div className="text-xs text-[#737373] mt-2">One branded PNG per service (Bookkeeping, Tax Prep, Sales Tax, Notary, Loan Signing, ITIN, Translation). Each scan tags the lead to its exact service line. Campaign tag above becomes the ZIP prefix.</div>
                <button onClick={downloadPrintSheet} className="w-full justify-center mt-3 inline-flex items-center gap-2 px-4 py-2.5 border border-[#0A0A0A] bg-white text-[#0A0A0A] uppercase tracking-wider text-xs font-semibold hover:bg-[#0A0A0A] hover:text-white transition" data-testid="qr-download-print-sheet"><FileText className="w-4 h-4" /> Download Print Sheet (PDF &middot; 8.5&times;11&quot;)</button>
                <div className="text-xs text-[#737373] mt-2">Single-page handout: all 7 service QRs in a 3&times;3 grid with EN/ES &ldquo;how to scan&rdquo; instructions and your contact card. Print, cut, hand out.</div>
              </div>
            </div>
          </div>
          <div className="border border-[#E5E5E5] bg-[#FDFBF7] p-4 flex items-center justify-center min-h-[440px]">
            {loading && <Loader2 className="w-6 h-6 animate-spin text-[#C4A252]" />}
            {!loading && previewUrl && (
              <img src={previewUrl} alt="QR code preview" className="max-w-full max-h-[420px] object-contain" data-testid="qr-preview" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const LeadsTab = () => {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s, b] = await Promise.all([
        api.get("/admin/leads", { params: { status: statusFilter, source: sourceFilter, q: q || undefined } }),
        api.get("/admin/leads/stats"),
        api.get("/admin/leads/source-breakdown"),
      ]);
      setLeads(l.data); setStats(s.data); setBreakdown(b.data);
    } catch { toast.error("Failed to load leads"); }
    finally { setLoading(false); }
  }, [statusFilter, sourceFilter, q]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { nav("/portal"); return; }
    load();
  }, [user, authLoading, nav, load]);

  const refreshActive = async (deleted = false) => {
    if (deleted) { setActiveLead(null); load(); return; }
    if (!activeLead) { load(); return; }
    try { const { data } = await api.get(`/admin/leads/${activeLead.id}`); setActiveLead(data); load(); }
    catch { load(); }
  };

  const sources = useMemo(() => [{ key: "all", en: "All Sources" }, ...SOURCES], []);

  return (
    <div className="rn3-container py-10" data-testid="leads-tab">
      <button onClick={() => nav("/admin")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 mb-4" data-testid="leads-back">
        <ArrowLeft className="w-3 h-3" /> Back to Admin
      </button>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="rn3-eyebrow">Lead Management · CRM</div>
          <h1 className="font-serif-display text-4xl mt-2">Leads</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              setDigestBusy(true);
              try {
                const { data } = await api.post("/admin/leads/overdue-digest/send", {});
                const note = data.mocked ? " (MOCKED — no Resend key)" : "";
                if (data.overdue_count + data.stale_count === 0) {
                  toast.success(`Digest sent to ${data.to}${note} — all caught up!`);
                } else {
                  toast.success(`Digest sent to ${data.to}${note} — ${data.overdue_count} overdue, ${data.stale_count} stale.`);
                }
              } catch (err) { toast.error(err.response?.data?.detail || "Digest failed"); }
              finally { setDigestBusy(false); }
            }}
            disabled={digestBusy}
            className="rn3-btn-gold"
            data-testid="lead-digest-btn"
            title="Email yourself a daily summary of overdue follow-ups + stale leads"
          >
            {digestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Send Digest
          </button>
          <button onClick={() => setShowQR(true)} className="rn3-btn-gold" data-testid="qr-open-btn" title="Generate a QR code for marketing">
            <QrCode className="w-4 h-4" /> QR Code
          </button>
          <InviteClientButton onInvited={() => load(true)} />
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="rn3-btn-primary" data-testid="lead-add-btn"><Plus className="w-4 h-4" /> New Lead</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8">
        <StatCard icon={Users} label="Total Leads" value={stats?.total ?? "—"} testid="stat-lead-total" />
        <StatCard icon={TrendingUp} label="New (7d)" value={stats?.new_this_week ?? "—"} testid="stat-lead-new" />
        <StatCard icon={Activity} label="Active Pipeline" value={stats?.active_pipeline ?? "—"} testid="stat-lead-active" />
        <StatCard icon={AlertTriangle} label="Overdue Follow-ups" value={stats?.overdue_follow_ups ?? "—"} accent={stats?.overdue_follow_ups > 0 ? "text-[#8B1C22]" : ""} testid="stat-lead-overdue" />
        <StatCard icon={Percent} label="Conversion %" value={stats ? `${stats.conversion_rate}%` : "—"} testid="stat-lead-conversion" />
      </div>

      <SourceBreakdownPanel
        breakdown={breakdown}
        open={showBreakdown}
        onToggle={() => setShowBreakdown((v) => !v)}
      />

      <div className="flex flex-wrap items-end gap-3 mt-8 border-t border-[#E5E5E5] pt-6">
        <div className="flex-1 min-w-[240px]">
          <label className="rn3-eyebrow block mb-2">Search</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone…" className="rn3-input w-full pl-9" data-testid="lead-search" />
          </div>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="rn3-input" data-testid="lead-filter-status">
            <option value="all">All Statuses</option>
            {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_LABELS[k].en}</option>)}
          </select>
        </div>
        <div>
          <label className="rn3-eyebrow block mb-2">Source</label>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="rn3-input" data-testid="lead-filter-source">
            {sources.map((s) => <option key={s.key} value={s.key}>{s.en}</option>)}
          </select>
        </div>
      </div>

      <div className="border border-[#E5E5E5] overflow-x-auto mt-4">
        <table className="w-full text-sm min-w-[900px]" data-testid="leads-table">
          <thead className="text-left text-xs uppercase tracking-widest text-[#737373] border-b border-[#E5E5E5] bg-[#FDFBF7]">
            <tr>
              <th className="py-3 px-3">Name</th>
              <th className="py-3 px-3">Contact</th>
              <th className="py-3 px-3">Service</th>
              <th className="py-3 px-3">Source</th>
              <th className="py-3 px-3">Status</th>
              <th className="py-3 px-3 text-right">Score</th>
              <th className="py-3 px-3">Follow-up</th>
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="8" className="py-12 text-center text-[#737373]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</td></tr>}
            {!loading && leads.length === 0 && <tr><td colSpan="8" className="py-16 text-center text-[#737373]">No leads match the current filters.</td></tr>}
            {leads.map((lead) => (
              <tr key={lead.id} className="border-b border-[#F3EFE9] hover:bg-[#FDFBF7] cursor-pointer" data-testid={`lead-row-${lead.id}`} onClick={() => setActiveLead(lead)}>
                <td className="py-2.5 px-3 font-medium">
                  {lead.name}
                  {lead.business_name && <div className="text-xs text-[#737373] font-normal italic mt-0.5">{lead.business_name}</div>}
                </td>
                <td className="py-2.5 px-3 text-xs text-[#737373]">
                  {lead.email && <div>{lead.email}</div>}
                  {lead.phone && <div>{lead.phone}</div>}
                </td>
                <td className="py-2.5 px-3 text-xs capitalize">{(lead.service_requested || "—").replace(/_/g, " ")}</td>
                <td className="py-2.5 px-3 text-xs">{SOURCES.find((s) => s.key === lead.source)?.en || lead.source}</td>
                <td className="py-2.5 px-3"><StatusPill status={lead.status} /></td>
                <td className="py-2.5 px-3 text-right tabular-nums font-medium">{lead.lead_score ?? 0}</td>
                <td className={`py-2.5 px-3 text-xs ${isOverdue(lead.follow_up_date) ? "text-[#8B1C22] font-bold" : "text-[#404040]"}`}>{lead.follow_up_date || "—"}{isOverdue(lead.follow_up_date) ? " ⚠" : ""}</td>
                <td className="py-2.5 px-3 text-right">
                  <button onClick={(e) => { e.stopPropagation(); setEditing(lead); setShowForm(true); }} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#0A0A0A]" data-testid={`lead-edit-${lead.id}`}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LeadFormModal open={showForm} onClose={() => { setShowForm(false); setEditing(null); }} existing={editing} onSaved={() => load()} />
      <LeadDetailDrawer open={!!activeLead} lead={activeLead} onClose={() => setActiveLead(null)} onUpdated={refreshActive} />
      <QRGeneratorModal open={showQR} onClose={() => setShowQR(false)} />
    </div>
  );
};

export default LeadsTab;
